import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Paperclip, Trash, Upload, FileText, X, Spinner } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PJ_TYPES = [
  { value: 'memoire_technique', label: 'Mémoire technique' },
  { value: 'planning', label: 'Planning prévisionnel' },
  { value: 'references', label: 'Références chantiers' },
  { value: 'sous_traitants', label: 'Sous-traitants' },
  { value: 'autre', label: 'Autre' },
];

/**
 * Sprint C #19 — Pièces jointes entreprise
 * Upload base64 → stockage backend
 */
export default function EntreprisePJUpload({ invitationId, items = [], onChanged, locked = false }) {
  const [uploading, setUploading] = useState(false);
  const [pendingType, setPendingType] = useState('memoire_technique');
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Fichier trop lourd (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      // Convert to base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(
        `${API_URL}/api/entreprise/consultations/${invitationId}/pieces-jointes`,
        { filename: file.name, content_base64: b64, type: pendingType },
        { withCredentials: true }
      );
      toast.success(`Fichier "${file.name}" ajouté`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePJ = async (id) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try {
      await axios.delete(`${API_URL}/api/entreprise/consultations/${invitationId}/pieces-jointes/${id}`, { withCredentials: true });
      toast.success('Pièce jointe supprimée');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="border border-[#E0E0DB] bg-white p-4" data-testid="entreprise-pj-section">
      <div className="flex items-center gap-2 mb-2">
        <Paperclip size={16} className="text-[#1E3A5F]" weight="duotone" />
        <h3 className="text-sm font-bold text-[#2C2C2C]">Pièces jointes ({items.length})</h3>
        <span className="text-[10px] text-[#9A9A9A]">Mémoire technique, planning, références…</span>
      </div>

      {!locked && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select
            value={pendingType}
            onChange={(e) => setPendingType(e.target.value)}
            className="border border-[#E0E0DB] px-2 py-1 text-xs rounded-none bg-white"
            data-testid="entreprise-pj-type-select"
          >
            {PJ_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" disabled={uploading} />
          <Button
            variant="outline" size="sm" className="rounded-none gap-1 text-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="entreprise-pj-upload-btn"
          >
            {uploading ? <Spinner size={14} className="animate-spin" /> : <Upload size={14} weight="bold" />}
            {uploading ? 'Envoi…' : 'Ajouter un fichier'}
          </Button>
          <span className="text-[10px] text-[#9A9A9A]">Max 8 Mo · PDF, DOCX, ZIP, …</span>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-[#9A9A9A] italic">Aucune pièce jointe</p>
      ) : (
        <ul className="divide-y divide-[#F0F0EE] border border-[#E0E0DB]" data-testid="entreprise-pj-list">
          {items.map((pj) => {
            const typeLabel = PJ_TYPES.find((t) => t.value === pj.type)?.label || pj.type;
            return (
              <li key={pj.id} className="px-3 py-2 flex items-center gap-2" data-testid={`entreprise-pj-item-${pj.id}`}>
                <FileText size={14} className="text-[#9A9A9A] flex-shrink-0" />
                <a href={`${API_URL}${pj.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A5F] hover:underline flex-1 truncate">
                  {pj.filename}
                </a>
                <span className="text-[10px] uppercase tracking-wide text-[#9A9A9A] bg-[#F5F5F3] px-2 py-0.5">
                  {typeLabel}
                </span>
                <span className="text-[10px] text-[#9A9A9A] whitespace-nowrap">{formatSize(pj.size)}</span>
                {!locked && (
                  <button onClick={() => removePJ(pj.id)} className="text-[#9A9A9A] hover:text-[#8B3A3A]" data-testid={`entreprise-pj-delete-${pj.id}`}>
                    <Trash size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
